/**
 * @fileoverview Work schedules: how much time a person is expected to record
 * on a given day (MOR-1458), and which days are short (MOR-1459, MOR-1460,
 * MOR-1461).
 *
 * A schedule is hours per weekday, set per person by an admin (Jess: 7.5
 * Monday to Thursday, 5.5 Friday; Paul: nothing on Fridays). A person with no
 * schedule is on the firm's one figure Monday to Friday, which is exactly
 * what V1 measured against, so nobody sees a change until an admin sets one.
 *
 * A day expects nothing when it is:
 * - outside the person's employment window (`startDate` / `endDate` on the
 *   schedule, or before the day they joined the firm on Morgan), or
 * - a public holiday in the firm's state (`convex/lib/businessDays.ts`), or
 * - a weekday the schedule gives 0 hours.
 *
 * "Missing" means a day up to YESTERDAY (today is never chased) that expects
 * time and has less recorded than expected. Drafts count as recorded: the
 * flag is about entering time, not approving it.
 *
 * Everything here is pure so the rule is tested exhaustively. Loading the
 * inputs is `missingTimesheets.ts`'s job. The stored shape,
 * `workScheduleValidator`, lives with the `timesheetProfiles` table in
 * `convex/timesheets/timesheetProfiles/schema.ts`.
 *
 * Used by:
 * - convex/timesheets/missingTimesheets.ts (queries)
 * - convex/timesheets/profiles.ts (setWorkSchedule)
 * - src/components/timesheets/weekGrid.ts (day strip figures)
 */

import type { Infer } from "convex/values";
import {
  isPublicHoliday,
  type AuJurisdiction,
  type ProclaimedHoliday,
} from "@cvx/lib/businessDays";
import { workScheduleValidator } from "@cvx/timesheets/timesheetProfiles/schema";

export const WEEKDAY_KEYS = [
  "mon",
  "tue",
  "wed",
  "thu",
  "fri",
  "sat",
  "sun",
] as const;
export type WeekdayKey = (typeof WEEKDAY_KEYS)[number];

export const WEEKDAY_LABELS: Record<WeekdayKey, string> = {
  mon: "Monday",
  tue: "Tuesday",
  wed: "Wednesday",
  thu: "Thursday",
  fri: "Friday",
  sat: "Saturday",
  sun: "Sunday",
};

/** Longest working day a schedule may hold, in hours. */
export const MAX_HOURS_PER_DAY = 24;

export type WorkSchedule = Infer<typeof workScheduleValidator>;
export type WeekdayHours = WorkSchedule["hours"];

/** The firm-wide figure Monday to Friday, weekends off: V1's rule. */
export function defaultWeekdayHours(dailyHours: number): WeekdayHours {
  return {
    mon: dailyHours,
    tue: dailyHours,
    wed: dailyHours,
    thu: dailyHours,
    fri: dailyHours,
    sat: 0,
    sun: 0,
  };
}

/** Sum of a week's hours. */
export function weeklyHours(hours: WeekdayHours): number {
  return WEEKDAY_KEYS.reduce((sum, key) => sum + hours[key], 0);
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Throws on anything a client could send that would poison a shortfall
 * calculation: a negative or absurd day, NaN, a window that ends before it
 * starts.
 */
export function assertValidSchedule(schedule: WorkSchedule): void {
  for (const key of WEEKDAY_KEYS) {
    const hours = schedule.hours[key];
    if (!Number.isFinite(hours) || hours < 0 || hours > MAX_HOURS_PER_DAY) {
      throw new Error(
        `${WEEKDAY_LABELS[key]} must be between 0 and ${MAX_HOURS_PER_DAY} hours`,
      );
    }
  }
  if (schedule.startDate !== undefined && !ISO_DATE.test(schedule.startDate)) {
    throw new Error("Start date must be YYYY-MM-DD");
  }
  if (schedule.endDate !== undefined && !ISO_DATE.test(schedule.endDate)) {
    throw new Error("End date must be YYYY-MM-DD");
  }
  if (
    schedule.startDate !== undefined &&
    schedule.endDate !== undefined &&
    schedule.endDate < schedule.startDate
  ) {
    throw new Error("End date must not be before the start date");
  }
}

/** Monday-first weekday key for an ISO date (UTC, like the rest of the module). */
export function weekdayKeyOf(isoDate: string): WeekdayKey {
  // JS: 0 = Sunday. Rotate so Monday is index 0.
  const dow = new Date(`${isoDate}T00:00:00Z`).getUTCDay();
  return WEEKDAY_KEYS[(dow + 6) % 7];
}

/** Everything `expectedHoursOn` needs about one person and their firm. */
export interface ScheduleInputs {
  /** The person's own schedule, if an admin has set one. */
  schedule: WorkSchedule | undefined;
  /** The firm's Monday-to-Friday figure, used when there is no schedule. */
  defaultDailyHours: number;
  /** The day the person joined the firm on Morgan; nothing is expected before it. */
  joinedOn: string | undefined;
  /** The firm's state or territory for public holidays. Undefined = national only. */
  jurisdiction: AuJurisdiction | undefined;
  /** Proclaimed holidays merged with admin adjustments; undefined = baseline. */
  proclaimed: ProclaimedHoliday[] | undefined;
}

/** Hours expected on `isoDate`, or 0 when nothing is. */
export function expectedHoursOn(
  inputs: ScheduleInputs,
  isoDate: string,
): number {
  const { schedule, joinedOn } = inputs;
  if (joinedOn !== undefined && isoDate < joinedOn) return 0;
  if (schedule?.startDate !== undefined && isoDate < schedule.startDate) {
    return 0;
  }
  if (schedule?.endDate !== undefined && isoDate > schedule.endDate) return 0;
  const hours =
    schedule?.hours ?? defaultWeekdayHours(inputs.defaultDailyHours);
  const forDay = hours[weekdayKeyOf(isoDate)];
  if (forDay <= 0) return 0;
  if (isPublicHoliday(isoDate, inputs.jurisdiction, inputs.proclaimed)) {
    return 0;
  }
  return forDay;
}

/** `expectedHoursOn` in billing units, rounded to the nearest whole unit. */
export function expectedUnitsOn(
  inputs: ScheduleInputs,
  isoDate: string,
  unitMinutes: number,
): number {
  return Math.round((expectedHoursOn(inputs, isoDate) * 60) / unitMinutes);
}

/** Every ISO date from `start` to `end` inclusive. Empty when end < start. */
export function eachDay(start: string, end: string): string[] {
  const out: string[] = [];
  const cursor = new Date(`${start}T00:00:00Z`);
  const last = new Date(`${end}T00:00:00Z`);
  while (cursor.getTime() <= last.getTime()) {
    out.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return out;
}

export interface DayBalance {
  date: string;
  expectedUnits: number;
  recordedUnits: number;
  /** expected - recorded, never below 0. */
  shortUnits: number;
}

/**
 * One row per day in [start, end] that expects time. `recordedByDay` holds
 * the person's units on each date (any status). Days that expect nothing are
 * omitted, so the caller can tell "off that day" from "recorded nothing".
 */
export function dayBalances(
  inputs: ScheduleInputs,
  start: string,
  end: string,
  unitMinutes: number,
  recordedByDay: ReadonlyMap<string, number>,
): DayBalance[] {
  return eachDay(start, end).flatMap((date) => {
    const expectedUnits = expectedUnitsOn(inputs, date, unitMinutes);
    if (expectedUnits === 0) return [];
    const recordedUnits = recordedByDay.get(date) ?? 0;
    return [
      {
        date,
        expectedUnits,
        recordedUnits,
        shortUnits: Math.max(0, expectedUnits - recordedUnits),
      },
    ];
  });
}

/** The days in [start, end], up to and including `lastChasedDay`, that are short. */
export function missingDays(
  inputs: ScheduleInputs,
  start: string,
  end: string,
  lastChasedDay: string,
  unitMinutes: number,
  recordedByDay: ReadonlyMap<string, number>,
): DayBalance[] {
  const chaseEnd = end < lastChasedDay ? end : lastChasedDay;
  if (chaseEnd < start) return [];
  return dayBalances(
    inputs,
    start,
    chaseEnd,
    unitMinutes,
    recordedByDay,
  ).filter((day) => day.shortUnits > 0);
}

/** The day before `todayIso`: the latest day anyone is chased for. */
export function yesterdayOf(todayIso: string): string {
  const d = new Date(`${todayIso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}
